import { useCallback, useMemo, type ReactElement } from 'react'
import { ButtonSave, Page } from '@components'
import {
  gameHasUnsavedChanges,
  participantSettingsHasChanges,
  useEditGameParticipants,
  useParticipantListState,
  useUpdateGame,
} from '@front/model'
import { useToastError } from '@hooks'
import { Text } from '@mantine/core'
import { CompetitionPageContainer } from 'app/routes/competition/CompetitionPageContainer'
import { GamePermission } from '../competition_.$gameId/GamePermission'
import { useGameContext } from '../competition_.$gameId/useGameContext'
import { PageContent } from './PageContent'
import { ParticipantsContext } from './context'
import { createFileRoute } from '@tanstack/react-router'

function CompetitionParticipant(): ReactElement {
  const { data, refetch, isFetching } = useGameContext()
  const { update, isUpdating, error: updateError } = useUpdateGame()
  useToastError(updateError)

  const state = useParticipantListState(data)
  const { participants, participantSettings } = state

  const { editParticipants, isBusy, error } = useEditGameParticipants()
  useToastError(error)

  const handleSave = useCallback(async (): Promise<void> => {
    let game = data.game
    if (participantSettingsHasChanges(participantSettings, data)) {
      game = await update({
        ...data.game,
        settings: participantSettings,
      })
    }
    const newFormParticipants = await editParticipants({
      game,
      oldParticipants: data.participants,
      newParticipants: participants,
    })
    state.setParticipants(newFormParticipants)
  }, [data, editParticipants, participantSettings, participants, state, update])

  const hasUnsavedChanges = useMemo(
    () => gameHasUnsavedChanges(participants, participantSettings, data),
    [participants, participantSettings, data]
  )

  return (
    <ParticipantsContext.Provider value={{ ...state, isBusy }}>
      <CompetitionPageContainer>
        <Page.Heading isFetching={isFetching} refetch={refetch} title='Participants'>
          {hasUnsavedChanges && <Text size='sm'>You have unsaved changes</Text>}
          <GamePermission>
            <ButtonSave disabled={!hasUnsavedChanges} loading={isBusy || isUpdating} onClick={handleSave} />
          </GamePermission>
        </Page.Heading>
        <PageContent />
      </CompetitionPageContainer>
    </ParticipantsContext.Provider>
  )
}

export const Route = createFileRoute('/competition_/$gameId/participant/')({
  component: CompetitionParticipant,
})
